import React from 'react';
import { View, Text, ScrollView } from 'react-native';
import { ActivityIndicator } from 'react-native-paper';
import { StyleSheet } from 'react-native-unistyles';
import { useRouter } from 'expo-router';
import SheetModal from '@/components/General/SheetModal';
import ContentLoader from '@/components/General/ContentLoader';
import Callout from '@/components/General/Callout';
import SolidButton from '@/components/General/SolidButton';
import Chip from '@/components/General/Chip';
import Unicon from '@/components/General/Unicon';
import Avatar from '@/components/People/Avatar';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import RoleResourcesList, { getRoleResources } from '@/components/Roles/RoleResourcesList';
import { Query } from '@/utils/react-query/Queries';
import { isRoleVisibleToUser } from '@/data/roles';
import { roleCounts } from '@/utils/folderPickerModel';
import usePlatform from '@/utils/hooks/usePlatform';

// ─── Info Row ───────────────────────────────────────────────────────────────
const InfoRow = ({ icon, label, value, valueColorVariant = 'text' }) => {
    if (!value && value !== false && value !== 0) return null;
    return (
        <View style={styles.infoRow}>
            <View style={styles.infoRowIcon}>
                <Unicon name={icon} size={18} colorVariant="textSecondary" />
            </View>
            <View style={styles.infoRowContent}>
                <Text style={styles.infoRowLabel}>{label}</Text>
                <Text style={styles.infoRowValue(valueColorVariant)}>{value}</Text>
            </View>
        </View>
    );
};

// ─── Section Label ──────────────────────────────────────────────────────────
const SectionLabel = ({ children }) => (
    <View style={styles.sectionLabelContainer}>
        <Text style={styles.sectionLabelText}>{children}</Text>
    </View>
);

// ─── Roles Info Content ─────────────────────────────────────────────────────
const RolesInfoContent = ({ itemId, onClose }) => {
    const router = useRouter();
    const { data: loggedInUser } = Query.people.loggedInUser();
    const { data: role, isFetching } = Query.roles.byId({
        id: itemId,
        options: { enabled: !!itemId },
    });
    const { data: roleFoldersData } = Query.roleFolders.all({ user: loggedInUser, options: { enabled: !!itemId } });

    // For dynamic roles, query members via the quick filter
    const { data: quickFilterMembers, isFetching: isFetchingMembers } = Query.people.all({
        filters: role?.QuickFilter?.filterJSON,
        filterLogic: role?.QuickFilter?.filterLogic,
        options: { enabled: (!isFetching && !!role?.quickFilterID) },
    });

    const folderName = React.useMemo(() => {
        if (!role?.folderID) return null;
        const folder = roleFoldersData?.allFolders?.find(f => f.id === role.folderID);
        return folder?.name;
    }, [role?.folderID, roleFoldersData]);

    const isDynamic = !!role?.quickFilterID;
    const members = isDynamic
        ? (quickFilterMembers?.pages?.flatMap(m => m.data) || [])
        : (role?.People || []);
    const permissions = role?.Permissions || [];
    const isAdmin = loggedInUser?.isAdmin;

    const goToEdit = () => {
        onClose?.();
        router.push({ pathname: '/roles/create', params: { id: role?.id, folderID: role?.folderID } });
    };

    if (isFetching) {
        return (
            <View style={styles.loadingContainer}>
                <ContentLoader containerHeight={300} spacingVertical={10} amount={5} />
            </View>
        );
    }

    if (!role) {
        return (
            <View style={styles.loadingContainer}>
                <EmptyListPlaceholder message="Could not load role info." />
            </View>
        );
    }

    return (
        <ScrollView style={styles.contentScroll} contentContainerStyle={styles.contentContainer} showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled">
            {/* Header */}
            <View style={styles.header}>
                <View style={styles.headerIcon}>
                    <Unicon name="people-circle-outline" size={28} colorVariant="primary" />
                </View>
                <View style={styles.headerText}>
                    <Text style={styles.headerTitle}>{role.name}</Text>
                    {!!role.description && (
                        <Text style={styles.headerSubtitle}>{role.description}</Text>
                    )}
                </View>
                <SolidButton
                    variant="muted"
                    type="primary"
                    size="sm"
                    text="Edit Role"
                    iconName="create"
                    iconBefore
                    onPress={goToEdit}
                    testID="RoleInfoEditButton"
                />
            </View>

            {/* Info Rows */}
            <View style={styles.infoSection}>
                <InfoRow
                    icon="folder-outline"
                    label="Folder"
                    value={folderName || 'No folder'}
                    valueColorVariant={folderName ? 'text' : 'textSecondary'}
                />
                <InfoRow
                    icon="shield-checkmark-outline"
                    label="Access Control"
                    value={role.isAccessControl ? 'Yes' : 'No'}
                    valueColorVariant={role.isAccessControl ? 'error' : 'textSecondary'}
                />
                <InfoRow
                    icon="sync-outline"
                    label="Type"
                    value={isDynamic ? 'Dynamic (filter-based)' : 'Static (manual members)'}
                    valueColorVariant={isDynamic ? 'info' : 'textSecondary'}
                />
                <InfoRow
                    icon="eye-outline"
                    label="Hidden"
                    value={role.isHidden ? 'Yes' : 'No'}
                    valueColorVariant={role.isHidden ? 'warning' : 'textSecondary'}
                />
            </View>

            {/* Dynamic callout */}
            {isDynamic && (
                <View style={styles.calloutWrapper}>
                    <Callout
                        type="info"
                        message="This role is dynamic. Members are determined by a filter and will change automatically."
                    />
                </View>
            )}

            {/* Permissions (admin only) */}
            {isAdmin && (
                <View style={styles.section}>
                    <SectionLabel>Permissions ({permissions.length})</SectionLabel>
                    {permissions.length === 0 ? (
                        <Text style={styles.emptyText}>No permissions assigned.</Text>
                    ) : (
                        <View style={styles.permissionsList}>
                            {permissions.map(p => (
                                <Chip key={p.id} icon="lock-closed-outline" iconSize={13}>{p.name}</Chip>
                            ))}
                        </View>
                    )}
                </View>
            )}

            {/* Resources — linked rows; onNavigate closes the modal before pushing */}
            <View style={styles.section}>
                <SectionLabel>Resources ({getRoleResources(role).length})</SectionLabel>
                {getRoleResources(role).length === 0 ? (
                    <Text style={styles.emptyText}>No shared resources.</Text>
                ) : (
                    <RoleResourcesList role={role} onNavigate={onClose} />
                )}
            </View>

            {/* Members */}
            <View style={styles.section}>
                <SectionLabel>Members{isDynamic && isFetchingMembers ? ' (loading…)' : ` (${members.length})`}</SectionLabel>
                {isFetchingMembers ? (
                    <View style={styles.membersLoading}>
                        <ActivityIndicator size="small" />
                    </View>
                ) : members.length === 0 ? (
                    <Text style={styles.emptyText}>{isDynamic ? 'No people match this filter.' : 'No members assigned.'}</Text>
                ) : (
                    <View style={styles.membersList}>
                        {members.map(person => (
                            <View key={person.id} style={styles.memberRow}>
                                <Avatar size={32} profile={person} disableBorder />
                                <View style={styles.memberInfo}>
                                    <Text style={styles.memberName}>
                                        {person.firstName} {person.lastName}
                                    </Text>
                                    {person.canManage && (
                                        <Chip icon="star" iconSize={10} colorVariant="info">Manager</Chip>
                                    )}
                                </View>
                            </View>
                        ))}
                    </View>
                )}
            </View>
        </ScrollView>
    );
};

// ─── Role Folder Info Content ───────────────────────────────────────────────
const RoleFolderInfoContent = ({ itemId, onClose }) => {
    const router = useRouter();
    const { data: loggedInUser } = Query.people.loggedInUser();
    const { data: folder, isFetching } = Query.roleFolders.byId({
        id: itemId,
        options: { enabled: !!itemId },
    });
    const { data: roleFoldersData } = Query.roleFolders.all({ user: loggedInUser, options: { enabled: !!itemId } });

    // Ancestor path for context (e.g. "Ministry / Youth / Leaders").
    const folderPathText = React.useMemo(() => {
        if (!folder || !roleFoldersData?.allFolders) return null;
        const byID = new Map(roleFoldersData.allFolders.map(f => [f.id, f]));
        const names = [];
        let current = byID.get(folder.id);
        while (current) {
            names.unshift(current.name);
            current = current.parentFolderID ? byID.get(current.parentFolderID) : null;
        }
        return names.join(' / ');
    }, [folder, roleFoldersData]);

    // The Roles(id, name, isHidden) embed is unfiltered — hidden role names
    // must not leak to viewers who can't see them (admins / role owners only).
    // Same rule the picker applies via getRolesInFolders.
    const roles = (folder?.Roles || []).filter(r => isRoleVisibleToUser(r, loggedInUser));
    const childFolderCount = roleFoldersData?.allFolders?.filter(f => f.parentFolderID === folder?.id)?.length || 0;

    // Aggregate member/permission/resource counts over this folder + all
    // descendant folders, using the picker's count-embedded allRoles list.
    // Same visibility filter as the picker so counts match what it advertises.
    const subtreeTotals = React.useMemo(() => {
        const allFolders = roleFoldersData?.allFolders;
        const allRoles = roleFoldersData?.allRoles;
        if (!folder || !allFolders || !allRoles) return null;
        const folderIDs = new Set([folder.id]);
        let grew = true;
        while (grew) {
            grew = false;
            for (const f of allFolders) {
                if (f.parentFolderID && folderIDs.has(f.parentFolderID) && !folderIDs.has(f.id)) {
                    folderIDs.add(f.id);
                    grew = true;
                }
            }
        }
        const totals = { members: 0, permissions: 0, resources: 0 };
        for (const role of allRoles) {
            if (!folderIDs.has(role.folderID) || !isRoleVisibleToUser(role, loggedInUser)) continue;
            const c = roleCounts(role);
            totals.members += c.members;
            totals.permissions += c.permissions;
            totals.resources += c.resources;
        }
        return totals;
    }, [folder, roleFoldersData, loggedInUser]);

    const goToEdit = () => {
        onClose?.();
        router.push({ pathname: '/roles/folders/create', params: { id: folder?.id } });
    };

    if (isFetching) {
        return (
            <View style={styles.loadingContainer}>
                <ContentLoader containerHeight={300} spacingVertical={10} amount={5} />
            </View>
        );
    }

    if (!folder) {
        return (
            <View style={styles.loadingContainer}>
                <EmptyListPlaceholder message="Could not load folder info." />
            </View>
        );
    }

    return (
        <ScrollView style={styles.contentScroll} contentContainerStyle={styles.contentContainer} showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled">
            {/* Header */}
            <View style={styles.header}>
                <View style={styles.headerIcon}>
                    <Unicon name="folder-outline" size={28} colorVariant="primary" />
                </View>
                <View style={styles.headerText}>
                    <Text style={styles.headerTitle}>{folder.name}</Text>
                    {!!folderPathText && folderPathText !== folder.name && (
                        <Text style={styles.headerSubtitle}>{folderPathText}</Text>
                    )}
                </View>
                <SolidButton
                    variant="muted"
                    type="primary"
                    size="sm"
                    text="Edit Folder"
                    iconName="create"
                    iconBefore
                    onPress={goToEdit}
                    testID="RoleFolderInfoEditButton"
                />
            </View>

            {/* Info Rows */}
            <View style={styles.infoSection}>
                <InfoRow
                    icon="people-circle-outline"
                    label="Roles"
                    value={roles.length}
                />
                <InfoRow
                    icon="folder-outline"
                    label="Subfolders"
                    value={childFolderCount}
                />
                {/* Subtree totals — same descendant scope the picker's folder
                    count chip covers. */}
                {subtreeTotals && (
                    <>
                        <InfoRow
                            icon="people-outline"
                            label="Members"
                            value={subtreeTotals.members}
                        />
                        <InfoRow
                            icon="key-outline"
                            label="Permissions"
                            value={subtreeTotals.permissions}
                        />
                        <InfoRow
                            icon="layers-outline"
                            label="Resources"
                            value={subtreeTotals.resources}
                        />
                    </>
                )}
            </View>

            {/* Roles in this folder */}
            <View style={styles.section}>
                <SectionLabel>Roles ({roles.length})</SectionLabel>
                {roles.length === 0 ? (
                    <Text style={styles.emptyText}>No roles in this folder.</Text>
                ) : (
                    <View style={styles.membersList}>
                        {roles.map(role => (
                            <View key={role.id} style={styles.memberRow}>
                                <Unicon name="people-circle-outline" size={22} colorVariant="textSecondary" />
                                <View style={styles.memberInfo}>
                                    <Text style={styles.memberName}>{role.name}</Text>
                                </View>
                            </View>
                        ))}
                    </View>
                )}
            </View>
        </ScrollView>
    );
};

// ─── Main Component ─────────────────────────────────────────────────────────
const SheetSelectorInfoModal = ({ type, itemId, isOpen, onClose }) => {
    const { isWeb } = usePlatform();

    const renderContent = () => {
        if (!itemId) return null;
        switch (type) {
            case 'roles':
                return <RolesInfoContent itemId={itemId} onClose={onClose} />;
            case 'roleFolders':
                return <RoleFolderInfoContent itemId={itemId} onClose={onClose} />;
            default:
                return (
                    <View style={styles.loadingContainer}>
                        <EmptyListPlaceholder message={`Info not available for type "${type}".`} />
                    </View>
                );
        }
    };

    return (
        <SheetModal
            title={type === 'roleFolders' ? 'Folder Info' : 'Role Info'}
            isOpen={isOpen}
            onClose={onClose}
            onCancel={onClose}
            cancelText="Close"
            snapPoints={isWeb ? undefined : ['85%']}
        >
            {renderContent()}
        </SheetModal>
    );
};

const styles = StyleSheet.create(({ colors, fonts, spacing, shadows, typography }) => ({
    contentScroll: {
        flex: 1,
    },
    contentContainer: {
        paddingBottom: spacing.xl,
        gap: spacing.lg,
    },
    loadingContainer: {
        padding: spacing.md,
        minHeight: 200,
    },
    // Header
    header: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 12,
    },
    headerIcon: {
        width: 48,
        height: 48,
        borderRadius: 12,
        backgroundColor: colors.primaryContainer,
        alignItems: 'center',
        justifyContent: 'center',
    },
    headerText: {
        flex: 1,
    },
    headerTitle: {
        fontSize: typography.h4,
        fontFamily: fonts.heading,
        color: colors.text,
    },
    headerSubtitle: {
        fontSize: typography.caption,
        color: colors.textSecondary,
        marginTop: 4,
    },
    // Info rows
    infoSection: {
        backgroundColor: colors.surface,
        borderRadius: 10,
        padding: spacing.sm,
        boxShadow: shadows.card,
    },
    infoRow: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 10,
        paddingHorizontal: spacing.sm,
        gap: 12,
    },
    infoRowIcon: {
        width: 32,
        alignItems: 'center',
    },
    infoRowContent: {
        flex: 1,
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    infoRowLabel: {
        fontSize: typography.body,
        color: colors.textSecondary,
        fontFamily: fonts.heading,
    },
    infoRowValue: (colorVariant) => ({
        fontSize: typography.body,
        color: colors[colorVariant] || colors.text,
        fontFamily: fonts.body,
        textAlign: 'right',
    }),
    // Callout
    calloutWrapper: {
        marginBottom: 0,
    },
    // Sections
    section: {},
    sectionLabelContainer: {
        marginBottom: spacing.sm,
    },
    sectionLabelText: {
        fontSize: typography.caption,
        fontFamily: fonts.bold,
        color: colors.textSecondary,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
    },
    // Permissions
    permissionsList: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 6,
    },
    // Members
    membersList: {
        gap: 4,
    },
    memberRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        paddingVertical: 6,
        paddingHorizontal: spacing.xs,
    },
    memberInfo: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    memberName: {
        fontSize: typography.body,
        fontFamily: fonts.body,
        color: colors.text,
    },
    membersLoading: {
        padding: spacing.md,
        alignItems: 'center',
    },
    emptyText: {
        fontSize: typography.caption,
        color: colors.textSecondary,
        fontStyle: 'italic',
        paddingVertical: spacing.sm,
    },
}));

export default SheetSelectorInfoModal;
